import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FormTextField } from '@skupreme/shared/ui/form';
import MarketPlaceService from '@utility/services/marketplace';
import { ToastMessage } from '@utility/toast';
import {
  ConnectIntro,
  ConnectNote,
  ConnectPopup,
  failureText,
} from './ConnectPopup';

const formSchema = z.object({
  accessToken: z.string().min(1, 'Access token is required'),
});

type TemuValues = z.infer<typeof formSchema>;

const EMPTY: TemuValues = { accessToken: '' };

type TemuConnectPopupProps = {
  open: boolean;
  closeModal: (success?: boolean) => void;
  marketplaceId: string;
  reload: () => void;
  /** The marketplace logo. */
  image?: string;
};

export function TemuConnectPopup({
  open,
  closeModal,
  marketplaceId,
  reload,
  image,
}: TemuConnectPopupProps) {
  const { control, handleSubmit, reset, clearErrors } = useForm<TemuValues>({
    resolver: zodResolver(formSchema),
    defaultValues: EMPTY,
    shouldUnregister: true,
  });

  const [integrating, setIntegrating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const close = (connected?: boolean) => {
    reset(EMPTY);
    clearErrors();
    setSuccess('');
    setError('');
    closeModal(connected);
  };

  const onSubmit = async (data: TemuValues) => {
    try {
      setIntegrating(true);
      setError('');

      await MarketPlaceService.connectTemu({
        marketplaceId,
        accessToken: data.accessToken.trim(),
      });

      setSuccess('TEMU connected successfully');
      reload();
      setTimeout(() => close(true), 2000);
    } catch (failure) {
      const message = failureText(failure, 'message', 'Connection failed');
      setError(message);
      ToastMessage.error(message);
    } finally {
      setIntegrating(false);
    }
  };

  return (
    <ConnectPopup
      open={open}
      title="TEMU Integration"
      onClose={() => close()}
      onSubmit={handleSubmit(onSubmit)}
      submitLabel={integrating ? 'Submitting...' : 'Submit'}
      isSubmitting={integrating}
      error={error}
      success={success}
    >
      <ConnectIntro logo={image}>
        Please paste your TEMU Access Token here
      </ConnectIntro>
      <FormTextField
        control={control}
        name="accessToken"
        label="Access Token"
        isRequired
      />
      <ConnectNote>
        To integrate TEMU, fetch your access token from your TEMU Seller App
        Store. <br /> Follow{' '}
        <a
          href="https://skupreme.com/knowledge-base/temu-integration"
          target="_blank"
          rel="noreferrer"
          className="sk:text-link"
        >
          TEMU Integration Guide
        </a>
        .
      </ConnectNote>
    </ConnectPopup>
  );
}
